class CartBundleRemoveButton extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.querySelector('button, a[role="button"]')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const bundleRow = this.closest('.cart-item--bundle');
      if (bundleRow) CartBundleManager.removeBundle(bundleRow);
    });
  }
}

customElements.define('cart-bundle-remove-button', CartBundleRemoveButton);

class CartBundleToggle extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    const button = this.querySelector('[data-bundle-toggle]');
    const panel = this.querySelector('[data-bundle-panel]');
    const label = this.querySelector('.cart-bundle-toggle__label');
    if (!button || !panel) return;

    const updateLabel = () => {
      if (!label) return;
      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      const showText = label.dataset.bundleShowText;
      const hideText = label.dataset.bundleHideText;

      if (showText && hideText) {
        label.textContent = isExpanded ? hideText : showText;
      }
    };

    updateLabel();

    button.addEventListener('click', () => {
      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
      panel.hidden = isExpanded;
      this.classList.toggle('cart-bundle-toggle--open', !isExpanded);
      updateLabel();
    });
  }
}

customElements.define('cart-bundle-toggle', CartBundleToggle);

const CartBundleManager = {
  getBundleData(bundleRow) {
    const lines =
      bundleRow?.dataset.bundleLines?.split(',').filter(Boolean).map((line) => parseInt(line, 10)) || [];
    const variantIds = bundleRow?.dataset.bundleVariantIds?.split(',').filter(Boolean) || [];
    const keys = bundleRow?.dataset.bundleKeys?.split(',').filter(Boolean) || [];
    return { lines, variantIds, keys };
  },

  isCartPage(bundleRow) {
    return Boolean(bundleRow?.closest('cart-items') && !bundleRow?.closest('cart-drawer-items'));
  },

  getDrawerSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  },

  getCartPageSectionsToRender() {
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');

    return [
      {
        id: 'main-cart-items',
        section: mainCartItems?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: mainCartFooter?.dataset.id,
        selector: '.js-contents',
      },
    ].filter((section) => section.section);
  },

  getSectionsToRender(bundleRow) {
    return this.isCartPage(bundleRow) ? this.getCartPageSectionsToRender() : this.getDrawerSectionsToRender();
  },

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  },

  setLoading(bundleRow, loading) {
    const line = bundleRow?.dataset.bundleLines?.split(',')[0] || '1';
    const spinner = bundleRow?.querySelector('.loading__spinner');
    const isCartPage = this.isCartPage(bundleRow);
    const itemsContainer = isCartPage
      ? document.getElementById('main-cart-items')
      : document.getElementById('CartDrawer-CartItems');
    const cartItemsElement = isCartPage ? document.querySelector('cart-items') : document.querySelector('cart-drawer-items');

    if (loading) {
      itemsContainer?.classList.add('cart__items--disabled');
      spinner?.classList.remove('hidden');
      cartItemsElement?.enableLoading?.(line);
    } else {
      itemsContainer?.classList.remove('cart__items--disabled');
      spinner?.classList.add('hidden');
      cartItemsElement?.disableLoading?.(line);
    }
  },

  async fetchCartState(bundleRow) {
    const sections = this.getSectionsToRender(bundleRow).map((section) => section.section);
    const [sectionsResponse, cartResponse] = await Promise.all([
      fetch(
        `${routes.cart_url}?sections=${sections.join(',')}&sections_url=${encodeURIComponent(window.location.pathname)}`
      ),
      fetch(`${routes.cart_url}.js`),
    ]);

    const sectionsData = await sectionsResponse.json();
    const cartData = await cartResponse.json();

    return {
      ...cartData,
      sections: sectionsData,
    };
  },

  renderCart(bundleRow, parsedState) {
    const isCartPage = this.isCartPage(bundleRow);

    if (isCartPage) {
      document.querySelector('cart-items')?.classList.toggle('is-empty', parsedState.item_count === 0);
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', parsedState.item_count === 0);
    } else {
      document.querySelector('cart-drawer-items')?.classList.toggle('is-empty', parsedState.item_count === 0);
      document.querySelector('cart-drawer')?.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    this.getSectionsToRender(bundleRow).forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id)?.querySelector(section.selector) ||
        document.getElementById(section.id);

      if (elementToReplace && parsedState.sections?.[section.section]) {
        elementToReplace.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      }
    });

    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState });
  },

  async removeBundle(bundleRow) {
    return this.updateBundleQuantity(0, bundleRow);
  },

  async updateBundleQuantity(quantity, bundleRow) {
    if (!bundleRow) return;

    const { lines, variantIds, keys } = this.getBundleData(bundleRow);
    this.setLoading(bundleRow, true);

    try {
      let parsedState = null;

      if (quantity === 0) {
        if (keys.length) {
          const updates = {};
          keys.forEach((key) => {
            updates[key] = 0;
          });

          const response = await fetch(`${routes.cart_update_url}`, {
            ...fetchConfig(),
            body: JSON.stringify({ updates }),
          });
          parsedState = await response.json();

          if (parsedState.status || parsedState.errors) {
            throw new Error(parsedState.description || parsedState.errors || window.cartStrings.error);
          }
        } else {
          const sortedLines = [...lines].sort((a, b) => b - a);

          for (const lineNumber of sortedLines) {
            const response = await fetch(`${routes.cart_change_url}`, {
              ...fetchConfig(),
              body: JSON.stringify({ line: lineNumber, quantity: 0 }),
            });
            parsedState = await response.json();

            if (parsedState.status) {
              throw new Error(parsedState.description || parsedState.message || window.cartStrings.error);
            }
          }
        }

        parsedState = await this.fetchCartState(bundleRow);
      } else if (variantIds.length) {
        const updates = {};
        variantIds.forEach((variantId) => {
          updates[variantId] = quantity;
        });

        const response = await fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            updates,
            sections: this.getSectionsToRender(bundleRow).map((section) => section.section),
            sections_url: window.location.pathname,
          }),
        });

        parsedState = JSON.parse(await response.text());

        if (parsedState.status || parsedState.errors || parsedState.message) {
          throw new Error(
            parsedState.description || parsedState.errors || parsedState.message || window.cartStrings.error
          );
        }
      }

      if (parsedState) {
        this.renderCart(bundleRow, parsedState);
      }
    } catch (error) {
      console.error(error);
      const errorEl = bundleRow.querySelector('.cart-item__error-text');
      if (errorEl) errorEl.textContent = error.message || window.cartStrings.error;
    } finally {
      this.setLoading(bundleRow, false);
    }
  },

  init() {
    if (this.initialized) return;
    this.initialized = true;

    document.addEventListener(
      'change',
      (event) => {
        const input = event.target.closest('.cart-item--bundle input[data-bundle-lines]');
        if (!input) return;

        event.stopImmediatePropagation();

        const quantity = parseInt(input.value, 10);
        const bundleRow = input.closest('.cart-item--bundle');

        if (Number.isNaN(quantity) || quantity < 0) {
          input.value = input.getAttribute('value') || '1';
          return;
        }

        this.updateBundleQuantity(quantity, bundleRow);
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        const removeButton = event.target.closest(
          '.cart-item--bundle cart-bundle-remove-button button, .cart-item--bundle cart-bundle-remove-button a[role="button"]'
        );
        if (!removeButton) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const bundleRow = removeButton.closest('.cart-item--bundle');
        if (bundleRow) this.removeBundle(bundleRow);
      },
      true
    );
  },
};

CartBundleManager.init();
